/**
 * ToolbarToggle Component
 *
 * Toggle button for toolbar boolean options.
 */

import type { ReactNode } from 'react';
import { EyeOff, Sparkles } from 'lucide-react';
import clsx from 'clsx';

export interface ToolbarToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  icon?: ReactNode;
  checkedIcon?: ReactNode;
  count?: number;
  compact?: boolean;
}

export function ToolbarToggle({
  checked,
  onChange,
  label,
  icon,
  checkedIcon,
  count,
  compact = false,
}: ToolbarToggleProps) {
  return (
    <button
      onClick={() => onChange(!checked)}
      title={label}
      className={clsx(
        'flex items-center gap-1.5 font-medium transition-all',
        compact
          ? 'w-7 h-7 justify-center rounded-md border border-border-primary/80'
          : 'px-2 py-1 text-[10px] sm:px-2.5 sm:py-1.5 sm:text-[11px] rounded-lg',
        checked
          ? 'bg-accent-emerald/10 text-accent-emerald border-accent-emerald/30'
          : 'text-text-quaternary hover:text-text-tertiary hover:bg-surface-hover border-transparent'
      )}
    >
      {checked ? checkedIcon || icon : icon}
      {!compact && <span className="hidden sm:inline">{label}</span>}
      {!compact && count !== undefined && count > 0 && (
        <span
          className={clsx(
            'px-1.5 py-0.5 rounded text-[9px] font-semibold tabular-nums',
            checked ? 'bg-accent-emerald/20' : 'bg-surface-tertiary'
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// Preset toggle for showing completed tasks
export interface ShowCompletedToggleProps {
  showCompleted: boolean;
  onToggle: (show: boolean) => void;
  completedCount?: number;
  compact?: boolean;
}

export function ShowCompletedToggle({
  showCompleted,
  onToggle,
  completedCount,
  compact = false,
}: ShowCompletedToggleProps) {
  return (
    <ToolbarToggle
      checked={showCompleted}
      onChange={onToggle}
      label="Completati"
      icon={<EyeOff className="w-3 h-3" />}
      checkedIcon={<Sparkles className="w-3 h-3" />}
      count={completedCount}
      compact={compact}
    />
  );
}
